import React, { useState, useEffect } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { motion, AnimatePresence } from 'motion/react';
import { generateCircuit, calculateIdealDays } from '../services/circuitEngine';
import { matrixService } from '../services/matrixService';
import { geocodePhoton } from '../services/photonService';
import { TourQuestionnaire, TouristSite, Settings, Location } from '../types';
import { Users, Dog, CheckCircle2, Star, MapPin, Phone, Clock, Accessibility, Info, ChevronLeft, Filter, Globe, Landmark, Trees, Footprints, Mountain, Coffee, UtensilsCrossed, Utensils, RefreshCw, X, Upload, Trash2, Sparkles, AlertTriangle, ThumbsUp, Smile, Annoyed, Frown, PawPrint, Ban, Baby, Route, Hourglass, FileText, Coins } from 'lucide-react';
import { PhotonAutocompleteInput } from './PhotonAutocompleteInput';
import { Step9 } from './tourist-tunnel/steps/Step9';
import { Step9_1 } from './tourist-tunnel/steps/Step9_1';
import { Step9_2 } from './tourist-tunnel/steps/Step9_2';
import { Step9_3 } from './tourist-tunnel/steps/Step9_3';
import { Step9_5 } from './tourist-tunnel/steps/Step9_5';
import { Step9_6 } from './tourist-tunnel/steps/Step9_6';
import { Step9_7 } from './tourist-tunnel/steps/Step9_7';
import { Step9_8 } from './tourist-tunnel/steps/Step9_8';
import { Step10_1 } from './tourist-tunnel/steps/Step10_1';
import { cn } from '../lib/utils';

const TOUR_SECTORS = [
  "Vis et Larzac",
  "Anduze et Cévennes",
  "Gorges de l'Hérault",
  "Littoral et Camargue",
  "Vestiges Romains",
  "Massif de l'Aigoual"
];

const CATEGORIES = [
  { id: 'patrimoine', label: 'Patrimoine' },
  { id: 'nature', label: 'Balades en nature' },
  { id: 'activites', label: 'Activité / Sport' },
  { id: 'randonnee', label: 'Randonnée' },
  { id: 'points_de_vue', label: 'Points de vue' },
  { id: 'terroir', label: 'Produits du terroir' },
  { id: 'grotte', label: 'Grotte' },
  { id: 'insolite', label: 'Insolite' },
  { id: 'baignade', label: 'Baignade' },
  { id: 'detente', label: 'Détente' }
];

export const calculateLocalCost = (selectedActivities: TouristSite[], passengers: TourQuestionnaire['passengers']) => {
  return selectedActivities.reduce((total, site) => {
    if (site.isFree) return total;
    const cost = 
      (passengers.adults + passengers.seniors) * (site.adultPrice || 0) +
      passengers.children6_12 * (site.child6_12Price || 0) +
      passengers.children3_6 * (site.child3_6Price || 0) +
      passengers.babies * (site.babyPrice || 0);
    return total + cost;
  }, 0);
};

interface TouristCircuitTunnelProps {
  questionnaire: TourQuestionnaire;
  setQuestionnaire: React.Dispatch<React.SetStateAction<TourQuestionnaire>>;
  onComplete: (circuit?: any) => void;
  onCancel: () => void;
  settings: Settings;
  sites: TouristSite[];
}

const getDistance = (loc1: { lat: number, lng: number }, loc2: { lat: number, lng: number }) => {
  const R = 6371; // Earth's radius in km
  const dLat = (loc2.lat - loc1.lat) * Math.PI / 180;
  const dLng = (loc2.lng - loc1.lng) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(loc1.lat * Math.PI / 180) * Math.cos(loc2.lat * Math.PI / 180) *
            Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const calculateTotalDuration = (selectedActivities: TouristSite[]) => {
  const visitTime = selectedActivities.reduce((acc, site) => acc + (site.durationMinutes || 60), 0);
  let totalDistanceKm = 0;
  for (let i = 0; i < selectedActivities.length - 1; i++) {
    totalDistanceKm += getDistance(selectedActivities[i].location, selectedActivities[i+1].location);
  }
  const travelTime = (totalDistanceKm / 50) * 60; // 50 km/h average speed
  return visitTime + travelTime;
};

export const TouristCircuitTunnel: React.FC<TouristCircuitTunnelProps> = ({
  questionnaire,
  setQuestionnaire,
  onComplete,
  onCancel,
  settings,
  sites
}) => {
  const [step, setStep] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedActivity, setSelectedActivityState] = useState<TouristSite | null>(null);
  const setSelectedActivity = (site: TouristSite | null, moment?: 'lunch' | 'dinner') => {
    setSelectedActivityState(site);
    if (moment) setCurrentMoment(moment);
  };
  const [hideUnsuitable, setHideUnsuitable] = useState(false);
  const [emblaRef] = useEmblaCarousel();
  const [sectorEmblaRef] = useEmblaCarousel();

  const [proposedCircuit, setProposedCircuit] = useState<any>(null);
  const [currentMoment, setCurrentMoment] = useState<'lunch' | 'dinner'>('lunch');
  const [driverHomeLocation, setDriverHomeLocation] = useState<Location | null>(null);
  const [orderedIds, setOrderedIds] = useState<string[]>([]);
  const [previousStep, setPreviousStep] = useState<number | null>(null);
  const [isHeavy, setIsHeavy] = useState(false);
  const [currentPlanningDay, setCurrentPlanningDay] = useState(1);
  const [detailReady, setDetailReady] = useState(false);
  const [idealDays, setIdealDays] = useState(1);

  useEffect(() => {
    if (step === 10) {
      setDetailReady(false);
      const timer = setTimeout(() => setDetailReady(true), 400);
      return () => clearTimeout(timer);
    }
  }, [step]);

  // Keep orderedIds in sync with selectedActivities
  useEffect(() => {
    setOrderedIds(prev => prev.filter(id => questionnaire.selectedActivities.includes(id)));
  }, [questionnaire.selectedActivities]);

  // Remove downstream data that becomes invalid after a user change
  const sanitizeQuestionnaire = (newQ: TourQuestionnaire, prevQ: TourQuestionnaire): TourQuestionnaire => {
    let q = { ...newQ };

    // Activities removed → clean their restaurants
    const removed = prevQ.selectedActivities.filter(id => !newQ.selectedActivities.includes(id));
    if (removed.length > 0 && q.selectedRestaurants) {
      const r = { ...q.selectedRestaurants };
      Object.keys(r).forEach(day => {
        const d = Number(day);
        if (removed.includes(r[d]?.lunch || '')) delete r[d].lunch;
        if (removed.includes(r[d]?.dinner || '')) delete r[d].dinner;
      });
      q.selectedRestaurants = r;
    }

    // Days reduced → clean data for removed days
    const newDays = newQ.logistics?.days || 1;
    const prevDays = prevQ.logistics?.days || 1;
    if (newDays < prevDays) {
      const cleanObj = (obj: Record<number, any>) => {
        const c = { ...obj };
        Object.keys(c).forEach(d => { if (Number(d) > newDays) delete c[Number(d)]; });
        return c;
      };
      if (q.selectedRestaurants) q.selectedRestaurants = cleanObj(q.selectedRestaurants);
      if (q.selectedAccommodations) q.selectedAccommodations = cleanObj(q.selectedAccommodations);
      if (q.dailyCatering) q.dailyCatering = cleanObj(q.dailyCatering);
    }

    // Switch to 'return' → remove evening meals and accommodations
    if (newQ.accommodationType === 'return' && prevQ.accommodationType !== 'return') {
      q.selectedAccommodations = {};
      if (q.dailyCatering) {
        const c = { ...q.dailyCatering };
        Object.keys(c).forEach(d => {
          if (c[Number(d)]) c[Number(d)] = { ...c[Number(d)], eveningMealIncluded: false };
        });
        q.dailyCatering = c;
      }
      if (q.selectedRestaurants) {
        const r = { ...q.selectedRestaurants };
        Object.keys(r).forEach(d => { if (r[Number(d)]?.dinner) delete r[Number(d)].dinner; });
        q.selectedRestaurants = r;
      }
    }

    // Departure >= 12h → remove lunch restaurants
    const newHour = parseInt(newQ.departureTime?.split(':')[0] || '0');
    const prevHour = parseInt(prevQ.departureTime?.split(':')[0] || '0');
    if (newHour >= 12 && prevHour < 12 && q.selectedRestaurants) {
      const r = { ...q.selectedRestaurants };
      Object.keys(r).forEach(d => { if (r[Number(d)]?.lunch) delete r[Number(d)].lunch; });
      q.selectedRestaurants = r;
    }

    return q;
  };

  const handleNext = async (acceptDrop: boolean | React.MouseEvent = false, questionnaireOverride?: Partial<TourQuestionnaire>) => {
    const shouldAcceptDrop = acceptDrop === true;
    const currentQuestionnaire = { ...questionnaire, ...questionnaireOverride };

    if (step === 9 || step === 9.1 || step === 9.5) {
      const selectedSites = sites.filter(s => currentQuestionnaire.selectedActivities.includes(s.id));

      let currentDriverHomeLocation = driverHomeLocation;
      if (!currentDriverHomeLocation && settings.driverHomeAddress) {
        try {
          const result = await geocodePhoton('', settings.driverHomeAddress.trim());
          if (result) {
            currentDriverHomeLocation = result.location;
            setDriverHomeLocation(result.location);
          }
        } catch (error) {
          console.error("Geocoding error:", error);
        }
      }

      if (!currentDriverHomeLocation) {
        setProposedCircuit({ error: "L'adresse du chauffeur n'est pas configurée ou n'a pas pu être géocodée. Veuillez contacter l'administrateur." });
        setStep(11);
        return;
      }

      try {
        // Build ordered sites list
        let sortedSites = [...selectedSites];
        if (orderedIds.length > 0) {
          const ordered = orderedIds.map(id => selectedSites.find(s => s.id === id)).filter(Boolean) as TouristSite[];
          const missing = selectedSites.filter(s => !orderedIds.includes(s.id));
          sortedSites = [...ordered, ...missing];
        }

        const tempMatrix = await matrixService.calculateTemporaryDistances(
          currentQuestionnaire.pickupLocation!, 'PICKUP_LOC', sortedSites, settings
        );
        const mergedMatrix = matrixService.getMergedMatrix(settings.distanceMatrices || {}, tempMatrix);

        // Step 9: calculate ideal days then go to dynamic planning (step 10.1)
        if (step === 9) {
          const needed = calculateIdealDays(currentQuestionnaire, sortedSites, settings, currentDriverHomeLocation, sites, mergedMatrix);
          setIdealDays(needed);
          setIsHeavy(false);
          setStep(10.1);
          return;
        }

        // Step 10.1 → onComplete was called, generate final circuit
        const finalResult = generateCircuit(currentQuestionnaire, sortedSites, settings, currentDriverHomeLocation, sites, mergedMatrix);

        if (finalResult.error) {
          setProposedCircuit(finalResult);
          setStep(11);
          return;
        }

        setProposedCircuit(finalResult);
        setCurrentPlanningDay(1);
        setStep(11);

      } catch (error: any) {
        console.error("Error generating circuit:", error);
        setProposedCircuit({ error: "Une erreur est survenue : " + (error.message || "Erreur inconnue") });
        setStep(11);
      }
      return;
    }
    if (step === 9.8) {
      if ((questionnaire.logistics?.days || 1) > 1 && !questionnaire.accommodationType) {
        setStep(9.2);
      } else {
        setStep(9.3);
        setCurrentMoment('lunch');
      }
      return;
    }
    if (step === 9.2) {
      setStep(9.3);
      setCurrentMoment('lunch');
      return;
    }
    if (step === 9.3) {
      setStep(9.6);
      return;
    }
    if (step === 9.6) {
      const totalDays = questionnaire.logistics?.days || 1;
      if (currentMoment === 'lunch') {
        if (currentPlanningDay < totalDays && questionnaire.accommodationType === 'hotel') {
          setStep(9.7);
        } else {
          if (currentPlanningDay < totalDays) {
            setCurrentPlanningDay(prev => prev + 1);
            setStep(9.8);
          } else {
            finishPlanning();
          }
        }
      } else {
        if (currentPlanningDay < totalDays) {
          setCurrentPlanningDay(prev => prev + 1);
          setStep(9.8);
        } else {
          finishPlanning();
        }
      }
      return;
    }
    if (step === 9.7) {
      setStep(9.3);
      setCurrentMoment('dinner');
      return;
    }
    if (step === 11) {
      onComplete(proposedCircuit);
      return;
    }
    if (step === 10) {
      onComplete();
      return;
    }
    if (step === 5) {
      setStep(7);
      return;
    }
    if (step < 10) setStep(step + 1);
    else onComplete();
  };

  const finishPlanning = () => {
    const selectedSites = sites.filter(s => questionnaire.selectedActivities.includes(s.id));
    let sortedSites = [...selectedSites];
    if (orderedIds.length > 0) {
      const ordered = orderedIds.map(id => selectedSites.find(s => s.id === id)).filter(Boolean) as TouristSite[];
      const missing = selectedSites.filter(s => !orderedIds.includes(s.id));
      sortedSites = [...ordered, ...missing];
    }
    // Reuse proposedCircuit if already valid (avoids recalc without mergedMatrix)
    if (proposedCircuit && !proposedCircuit.error) {
      setStep(11);
      return;
    }
    if (driverHomeLocation) {
      const result = generateCircuit(questionnaire, sortedSites, settings, driverHomeLocation, sites);
      setProposedCircuit(result);
    }
    setStep(11);
  };

  const handleBack = () => {
    if (step === 9.1) { setStep(9); return; }
    if (step === 9.5) { setStep(9.1); return; }
    if (step === 9.8) {
      if (currentPlanningDay > 1) {
        setCurrentPlanningDay(prev => prev - 1);
        setCurrentMoment(questionnaire.accommodationType === 'hotel' ? 'dinner' : 'lunch');
        setStep(9.6);
      } else {
        if (isHeavy) setStep(9.5);
        else if ((questionnaire.logistics?.days || 1) > 1) setStep(9.1);
        else setStep(9);
      }
      return;
    }
    if (step === 9.2) { setStep(9.8); return; }
    if (step === 9.3) {
      if (currentMoment === 'lunch') setStep(9.8);
      else setStep(9.7);
      return;
    }
    if (step === 9.6) { setStep(9.3); return; }
    if (step === 9.7) { setCurrentMoment('lunch'); setStep(9.6); return; }
    if (step === 10.1) { setStep(9); return; }
    if (step === 11) {
      setStep(10.1);
      return;
    }
    if (step === 10) {
      setStep(previousStep || 9);
      setSelectedActivity(null);
      setPreviousStep(null);
      return;
    }
    if (step === 5) { setStep(4); return; }
    if (step === 7) { setStep(5); return; }
    if (step > 1) setStep(step - 1);
    else onCancel();
  };

  const adjustPassengersForAnimal = (passengers: TourQuestionnaire['passengers'], animalPlaces: number) => {
    let total = passengers.seniors + passengers.adults + passengers.children6_12 + passengers.children3_6 + passengers.babies;
    if (total + animalPlaces <= 6) return passengers;

    let newPassengers = { ...passengers };
    let toRemove = total + animalPlaces - 6;

    const order: (keyof typeof passengers)[] = ['babies', 'children3_6', 'children6_12', 'adults', 'seniors'];
    for (const type of order) {
      while (toRemove > 0 && newPassengers[type] > 0) {
        newPassengers[type]--;
        toRemove--;
      }
    }
    return newPassengers;
  };

  const calculateSitePrice = (site: TouristSite) => {
    const adults = (questionnaire.passengers.adults || 0) + (questionnaire.passengers.seniors || 0);
    const c6_12 = questionnaire.passengers.children6_12 || 0;
    const c3_6 = questionnaire.passengers.children3_6 || 0;
    const babies = questionnaire.passengers.babies || 0;

    return (site.adultPrice || 0) * adults +
           (site.child6_12Price || 0) * c6_12 +
           (site.child3_6Price || 0) * c3_6 +
           (site.babyPrice || 0) * babies;
  };

  const renderStep = () => {
    console.log("Current step:", step);
    const getSiteScore = (site: TouristSite) => {
      let reasons: string[] = [];
      let isUnsuitable = false;
      let forcedColor: 'green' | 'yellow' | 'orange' | 'red' | null = null;

      const hasYoungChildren = (questionnaire.passengers?.children3_6 || 0) > 0 || (questionnaire.passengers?.babies || 0) > 0;
      const hasDog = questionnaire.hasAnimal === true;
      const hasMotionSickness = questionnaire.motionSickness === true;
      const isPMR = questionnaire.pmr === true;

      // --- 1. ROUGE (Incompatibilité majeure) ---
      // L'activité est totalement inadaptée et ne doit pas être proposée (ou très fortement déconseillée).
      if (isPMR && !site.isHandicapAccessible) {
        forcedColor = 'red';
        reasons.push("Non accessible PMR");
        isUnsuitable = true;
      }
      if (site.notSuitableForChildren && hasYoungChildren) {
        forcedColor = 'red';
        reasons.push("Inadapté aux enfants de moins de 6 ans");
        isUnsuitable = true;
      }

      // --- 2. ORANGE (Contre-indication ou aucune thématique) ---
      // L'activité présente une restriction mineure ou ne correspond pas du tout aux goûts du client.
      if (forcedColor !== 'red') {
        let hasOrangeRestriction = false;
        
        if (site.dogsNotAllowed && hasDog) {
          forcedColor = 'orange';
          reasons.push("Animaux interdits");
          hasOrangeRestriction = true;
        }
        if (site.isWindingRoad && hasMotionSickness) {
          forcedColor = 'orange';
          reasons.push("Route sinueuse (Mal des transports)");
          hasOrangeRestriction = true;
        }

        // Vérification des thématiques (préférences du client)
        const siteCategories = site.categories || [];
        const preferences = questionnaire.preferences || {};
        let bestRank = 99; // 99 signifie "non classé"
        let hasRankedCategory = false;

        siteCategories.forEach((cat) => {
          const rank = preferences[cat as keyof typeof preferences];
          if (rank !== null && rank !== undefined) {
            hasRankedCategory = true;
            if (rank < bestRank) {
              bestRank = rank; // On garde le meilleur classement (le plus petit chiffre)
            }
          }
        });

        if (!hasOrangeRestriction) {
          if (!hasRankedCategory) {
            // Le site ne correspond à AUCUNE des thématiques classées par le client
            forcedColor = 'orange';
            reasons.push("Ne correspond pas à vos thèmes de prédilection");
          } else {
            // --- 3. VERT ou JAUNE ---
            // Le site correspond à au moins une thématique et n'a pas de restriction Rouge/Orange.
            if (bestRank === 1) {
              // 🟢 VERT (Idéal) : Correspond au choix numéro 1 du client
              forcedColor = 'green';
              reasons.push("Correspond parfaitement à votre thématique favorite.");
            } else {
              // 🟡 JAUNE (Adapté) : Correspond à un choix secondaire (2 à 8)
              forcedColor = 'yellow';
              reasons.push("Correspond à vos centres d'intérêt.");
            }
          }
        }
      }

      // --- 4. Logique spécifique pour la Restauration ---
      // Si le site est un restaurant/snack, on affine la couleur en fonction des choix de repas.
      const isRestaurantSite = (site.categories || []).includes('restauration') || site.cateringType === 'restaurant' || site.cateringType === 'snack';
      if (isRestaurantSite && forcedColor !== 'red') {
          const userCatering = questionnaire.catering?.type;
          const siteForks = site.forks || 0;
          const isSiteSnack = siteForks === 0; // "il y a pas de fourchette"
          const isSiteRestaurant = siteForks > 0;
          
          if (userCatering === 'pique-nique') {
            if (isSiteSnack) {
              forcedColor = 'orange';
              reasons.push("Snack (possibilité d'acheter des sandwichs)");
            } else if (isSiteRestaurant) {
              forcedColor = 'red';
              reasons.push("Restaurant (incompatible avec pique-nique)");
              isUnsuitable = true;
            } else {
              if (forcedColor !== 'orange') forcedColor = 'green';
            }
          } else if (userCatering === 'snack') {
            if (isSiteRestaurant) {
              forcedColor = 'red';
              reasons.push("Restaurant (vous avez choisi snack/sandwicherie)");
              isUnsuitable = true;
            } else if (isSiteSnack) {
              forcedColor = 'green';
            }
          } else if (userCatering === 'restaurant') {
            if (isSiteSnack) {
              forcedColor = 'red';
              reasons.push("Snack (vous avez demandé un restaurant)");
              isUnsuitable = true;
            } else if (isSiteRestaurant) {
              // Vérification du standing (nombre de fourchettes)
              const userForks = questionnaire.catering.standing || 1;
              const diff = Math.abs(userForks - siteForks);
              
              if (diff === 0) {
                forcedColor = 'green';
              } else if (diff === 1) {
                forcedColor = 'yellow';
                reasons.push("Différence d'une fourchette");
              } else if (diff === 2) {
                forcedColor = 'orange';
                reasons.push("Différence de deux fourchettes");
              } else {
                forcedColor = 'red';
                reasons.push("Différence de standing trop importante");
                isUnsuitable = true;
              }
            }
          }
      }

      // Sécurité : si aucune couleur n'a été attribuée (ne devrait pas arriver), on met Jaune par défaut.
      if (!forcedColor) forcedColor = 'yellow';

      // --- 5. Calcul du score numérique pour le tri ---
      // Ce score permet de trier la liste : Vert en premier, puis Jaune (trié par préférence), puis Orange, puis Rouge.
      let score = 0;
      if (forcedColor === 'green') {
        score = 1000; // Les Verts sont toujours en haut
      } else if (forcedColor === 'yellow') {
        // Pour les Jaunes, on trie selon le classement de la thématique (de 2 à 8)
        const siteCategories = site.categories || [];
        let bestRank = 99;
        siteCategories.forEach((cat) => {
          const rank = questionnaire.preferences[cat as keyof typeof questionnaire.preferences];
          if (rank !== null && rank !== undefined && rank < bestRank) {
            bestRank = rank;
          }
        });
        // Plus le rang est petit (proche de 1), plus le score est élevé.
        // Ex: rang 2 -> score = 500 + (10 - 2) = 508
        // Ex: rang 8 -> score = 500 + (10 - 8) = 502
        score = 500 + (10 - bestRank); 
      } else if (forcedColor === 'orange') {
        score = 100; // Les Oranges sont en bas
      } else if (forcedColor === 'red') {
        score = 0;   // Les Rouges sont tout en bas (et souvent masqués)
      }

      return { score, reasons, isUnsuitable, forcedColor };
    };

    const getRestaurantScore = (site: TouristSite) => {
      const choice = questionnaire.catering.type;
      const standing = questionnaire.catering.standing || 1;
      
      if (choice === 'pique-nique') return { color: 'red', score: 0, reason: "Vous avez choisi pique-nique." };
      
      const isSnack = site.cateringType === 'snack' || site.forks === 0;
      const isRestaurant = site.cateringType === 'restaurant' && site.forks && site.forks > 0;

      if (choice === 'snack') {
        if (isSnack) return { color: 'green', score: 3, reason: "Correspond parfaitement à votre choix de restauration rapide (Snack)." };
        return { color: 'red', score: 0, reason: "Ne correspond pas à votre choix de restauration rapide." };
      }

      if (choice === 'restaurant') {
        if (isSnack) return { color: 'red', score: 0, reason: "Vous avez demandé un restaurant, cet établissement est un snack." };
        if (isRestaurant) {
          const diff = Math.abs(site.forks! - standing);
          if (diff === 0) return { color: 'green', score: 3, reason: `Correspond exactement au standing demandé (${standing} fourchette${standing > 1 ? 's' : ''}).` };
          if (diff === 1) return { color: 'yellow', score: 2, reason: `Standing légèrement différent de votre demande (${site.forks} fourchette${site.forks! > 1 ? 's' : ''}).` };
          if (diff === 2) return { color: 'orange', score: 1, reason: `Standing très différent de votre demande (${site.forks} fourchette${site.forks! > 1 ? 's' : ''}).` };
        }
      }
      
      return { color: 'red', score: 0, reason: "Ne correspond pas à vos critères." };
    };

    const getScoreColor = (color: string) => {
      if (color === 'green') return 'bg-emerald-500/20 border-emerald-500/40'; // Vert
      if (color === 'yellow') return 'bg-yellow-400/10 border-yellow-400/30'; // Jaune
      if (color === 'orange') return 'bg-orange-500/10 border-orange-500/30'; // Orange
      if (color === 'red') return 'bg-red-500/10 border-red-500/30'; // Rouge
      return 'bg-zinc-900/50 border-zinc-800';
    };

    const getScoreBadge = (color: string) => {
      if (color === 'green') return <div title="Idéal"><Smile className="w-8 h-8 shrink-0 text-emerald-400" strokeWidth={1.5} /></div>;
      if (color === 'yellow') return <div title="Très bien"><Smile className="w-8 h-8 shrink-0 text-yellow-400" strokeWidth={1.5} /></div>;
      if (color === 'orange') return <div title="Moyen"><Annoyed className="w-8 h-8 shrink-0 text-orange-400" strokeWidth={1.5} /></div>;
      if (color === 'red') return <div title="Moins adapté"><Frown className="w-8 h-8 shrink-0 text-red-500" strokeWidth={1.5} /></div>;
      return null;
    };

    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <h2 className="text-3xl font-bold text-white">{settings.touristCircuitsTitle || "Découvrez les Cévennes autrement"}</h2>
            <p className="text-zinc-400">{settings.touristCircuitsText || "Profitez du confort de notre Peugeot 5008 Bleu et de notre expertise locale pour un circuit inoubliable."}</p>
            <button onClick={handleNext} className="w-full py-3 bg-emerald-500 text-white rounded-xl font-semibold text-sm hover:bg-emerald-600 transition-colors shadow-[0_0_15px_rgba(16,185,129,0.4)]">
              {settings.touristCircuitsBtnText || "Commencer ma réservation"}
            </button>
          </div>
        );
      case 2:
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const year = tomorrow.getFullYear();
        const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
        const day = String(tomorrow.getDate()).padStart(2, '0');
        const minDate = `${year}-${month}-${day}`;
        const isStep2Valid = questionnaire.date && questionnaire.pickupLocation && questionnaire.departureTime;

        return (
          <div className="flex flex-col h-full bg-zinc-950 overflow-hidden relative">
            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6 pb-32 custom-scrollbar">
              <h2 className="text-2xl font-bold text-white">Logistique de départ</h2>
              {/* Form fields for Date, Location, Time */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-1">Indiquez la ville de votre lieu de séjour</label>
                  <PhotonAutocompleteInput 
                    placeholder="Ville ou lieu de vacances" 
                    onPlaceSelect={(location) => setQuestionnaire(prev => ({ ...prev, pickupLocation: location }))}
                    defaultValue={questionnaire.pickupLocation?.address || ''}
                    inputClassName="bg-zinc-900 border-white text-white focus:outline-none focus:ring-1 focus:ring-white"
                    allowedDepartments={['30', '34', '48', '12']}
                    citiesOnly={true}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-1">Date du circuit</label>
                  <input 
                    type="date" 
                    min={minDate}
                    value={questionnaire.date || ''}
                    onChange={(e) => setQuestionnaire(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full bg-zinc-900 border border-white rounded-xl p-3 text-white focus:outline-none focus:ring-1 focus:ring-white" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-400 mb-1">Heure de départ souhaitée</label>
                  <input 
                    type="time" 
                    value={questionnaire.departureTime || ''}
                    onChange={(e) => setQuestionnaire(prev => ({ ...prev, departureTime: e.target.value }))}
                    className="w-full bg-zinc-900 border border-white rounded-xl p-3 text-white focus:outline-none focus:ring-1 focus:ring-white" 
                  />
                </div>
              </div>
            </div>
            <div className="absolute bottom-0 left-0 right-0 z-30 bg-zinc-950/90 backdrop-blur-xl border-t border-white/10">
              <div className="max-w-7xl mx-auto p-4 md:p-6 flex gap-4">
                <button onClick={handleBack} className="flex-1 py-3 bg-zinc-800 text-white rounded-xl font-semibold text-sm hover:bg-zinc-700 transition-colors">Retour</button>
                <button 
                  onClick={handleNext} 
                  disabled={!isStep2Valid}
                  className={`flex-1 py-3 rounded-xl font-semibold text-sm transition-colors ${isStep2Valid ? 'bg-emerald-500 text-white hover:bg-emerald-600' : 'bg-zinc-700 text-zinc-500 cursor-not-allowed'}`}
                >
                  Suivant
                </button>
              </div>
            </div>
          </div>
        );
      case 3:
        const isAnimalValid = questionnaire.hasAnimal === false || (
          questionnaire.hasAnimal === true && 
          questionnaire.animalSize !== null && 
          questionnaire.animalCount > 0 && 
          questionnaire.animalRace.trim() !== ''
        );

        const isStep3Valid = questionnaire.hasAnimal !== null && isAnimalValid;

        const handleAnimalSizeChange = (size: 'mini' | 'petit' | 'moyen' | 'gros') => {
          setQuestionnaire(prev => {
            let newAnimalCount = prev.animalCount;
            if (size === 'gros' || size === 'moyen') {
              newAnimalCount = Math.min(prev.animalCount, 1);
            } else if (size === 'petit') {
              newAnimalCount = Math.min(prev.animalCount, 2);
            }
            if (newAnimalCount === 0) newAnimalCount = 1;

            let placesNeeded = 0;
            if (size === 'gros') placesNeeded = newAnimalCount * 2;
            if (size === 'moyen') placesNeeded = newAnimalCount * 1;
            if (size === 'petit') placesNeeded = newAnimalCount > 0 ? 1 : 0;
            
            return {
              ...prev,
              animalSize: size,
              animalCount: newAnimalCount,
              passengers: adjustPassengersForAnimal(prev.passengers, placesNeeded)
            };
          });
        };

        const handleHasAnimalChange = (hasAnimal: boolean) => {
          setQuestionnaire(prev => {
            const newPassengers = hasAnimal ? adjustPassengersForAnimal(prev.passengers, 0) : prev.passengers; // 0 places for no animal
            return {
              ...prev,
              hasAnimal,
              animalSize: hasAnimal ? (prev.animalSize || 'moyen') : null,
              animalCount: hasAnimal ? (prev.animalCount || 1) : 0,
              animalRace: hasAnimal ? prev.animalRace : '',
              passengers: newPassengers
            };
          });
        };

        const updateAnimalCount = (increment: boolean) => {
          setQuestionnaire(prev => {
            const current = prev.animalCount;
            let max = 6; // Default for mini
            if (prev.animalSize === 'gros' || prev.animalSize === 'moyen') max = 1;
            if (prev.animalSize === 'petit') max = 2;
            
            let newCount = current;
            if (increment && current < max) {
              newCount = current + 1;
            } else if (!increment && current > 0) {
              newCount = current - 1;
            }

            let placesNeeded = 0;
            if (prev.animalSize === 'gros') placesNeeded = newCount * 2;
            if (prev.animalSize === 'moyen') placesNeeded = newCount * 1;
            if (prev.animalSize === 'petit') placesNeeded = newCount > 0 ? 1 : 0;

            return { 
              ...prev, 
              animalCount: newCount,
              passengers: adjustPassengersForAnimal(prev.passengers, placesNeeded)
            };
          });
        };

        return (
          <div className="flex flex-col h-full bg-zinc-950 overflow-hidden relative">
            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6 pb-32 custom-scrollbar">
              <h2 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                <Dog className="w-6 h-6 text-emerald-400" />
                Animaux
              </h2>
              
              <div className="space-y-2 bg-zinc-800/60 p-3 rounded-xl border border-zinc-600/50">
                <p className="text-base text-white font-semibold">🐾 Avez-vous des animaux ?</p>
                <div className="flex gap-2">
                  <button 
                    onClick={() => handleHasAnimalChange(false)}
                    className={`flex-1 py-2 border rounded-xl text-sm font-bold transition-all ${questionnaire.hasAnimal === false ? 'bg-emerald-500/20 border-emerald-400 text-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.2)]' : 'bg-zinc-900 border-zinc-700 text-zinc-300 hover:border-zinc-500 hover:text-white'}`}
                  >
                    NON
                  </button>
                  <button 
                    onClick={() => handleHasAnimalChange(true)}
                    className={`flex-1 py-2 border rounded-xl text-sm font-bold transition-all ${questionnaire.hasAnimal === true ? 'bg-emerald-500/20 border-emerald-400 text-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.2)]' : 'bg-zinc-900 border-zinc-700 text-zinc-300 hover:border-zinc-500 hover:text-white'}`}
                  >
                    OUI
                  </button>
                </div>
              </div>

            {questionnaire.hasAnimal === true && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="space-y-3 bg-zinc-800/60 p-3 rounded-xl border border-emerald-500/30">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-white">Taille du chien</label>
                  <div className="grid grid-cols-4 gap-2">
                    {(['mini', 'petit', 'moyen', 'gros'] as const).map(size => {
                      return (
                        <button
                          key={size}
                          onClick={() => handleAnimalSizeChange(size)}
                          className={`py-1.5 px-2 rounded-lg text-xs font-bold capitalize transition-all border ${questionnaire.animalSize === size ? 'bg-emerald-500/20 border-emerald-400 text-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.2)]' : 'bg-zinc-900 border-zinc-700 text-zinc-300 hover:border-zinc-500 hover:text-white'}`}
                        >
                          {size}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {questionnaire.animalSize && (
                  <div className="flex items-center justify-between bg-zinc-900/80 p-2 rounded-lg border border-zinc-700">
                    <span className="text-sm text-white font-semibold">Nombre de chiens</span>
                    <div className="flex items-center gap-3">
                      <button 
                        onClick={() => updateAnimalCount(false)}
                        disabled={questionnaire.animalCount <= 0}
                        className="w-7 h-7 rounded-full bg-zinc-700 text-white flex items-center justify-center disabled:opacity-50 text-base font-bold hover:bg-zinc-600"
                      >-</button>
                      <span className="text-base font-bold text-emerald-400 w-4 text-center">{questionnaire.animalCount}</span>
                      <button 
                        onClick={() => updateAnimalCount(true)}
                        className="w-7 h-7 rounded-full bg-zinc-700 text-white flex items-center justify-center disabled:opacity-50 text-base font-bold hover:bg-zinc-600"
                      >+</button>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-white mb-1">Race de l'animal</label>
                  <input 
                    type="text" 
                    value={questionnaire.animalRace || ''}
                    onChange={(e) => setQuestionnaire(prev => ({ ...prev, animalRace: e.target.value }))}
                    className="w-full bg-zinc-900 border border-zinc-600 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400/50 transition-all"
                    placeholder="Ex: Berger Australien"
                  />
                </div>
              </motion.div>
            )}

            </div>
            <div className="absolute bottom-0 left-0 right-0 z-30 bg-zinc-950/90 backdrop-blur-xl border-t border-white/10">
              <div className="max-w-7xl mx-auto p-4 md:p-6 flex gap-3">
                <button onClick={handleBack} className="flex-1 py-3 bg-zinc-800 text-white rounded-xl font-semibold text-sm hover:bg-zinc-700 transition-colors border border-zinc-600">Retour</button>
                <button 
                  onClick={handleNext} 
                  disabled={!isStep3Valid}
                  className={`flex-1 py-3 rounded-xl font-semibold transition-all text-sm ${isStep3Valid ? 'bg-emerald-500 text-white hover:bg-emerald-400 shadow-[0_0_15px_rgba(52,211,153,0.4)]' : 'bg-zinc-800 text-zinc-500 border border-zinc-700 cursor-not-allowed'}`}
                >
                  Suivant
                </button>
              </div>
            </div>
          </div>
        );
      case 4:
        const animalPlacesTaken = questionnaire.hasAnimal === true ? (() => {
          if (questionnaire.animalSize === 'gros') return questionnaire.animalCount * 2;
          if (questionnaire.animalSize === 'moyen') return questionnaire.animalCount * 1;
          if (questionnaire.animalSize === 'petit') return questionnaire.animalCount > 0 ? 1 : 0;
          return 0;
        })() : 0;

        const passengers = questionnaire.passengers || { seniors: 0, adults: 0, children6_12: 0, children3_6: 0, babies: 0 };
        const totalPassengers = passengers.seniors + passengers.adults + passengers.children6_12 + passengers.children3_6 + passengers.babies;
        const placesRestantes = 6 - animalPlacesTaken - totalPassengers;

        const isStep4Valid = (passengers.seniors + passengers.adults >= 1);

        const updatePassenger = (type: keyof typeof questionnaire.passengers, increment: boolean) => {
          setQuestionnaire(prev => {
            const currentPassengers = prev.passengers || { seniors: 0, adults: 0, children6_12: 0, children3_6: 0, babies: 0 };
            const current = currentPassengers[type];
            if (increment && placesRestantes > 0) {
              return { ...prev, passengers: { ...currentPassengers, [type]: current + 1 } };
            } else if (!increment && current > 0) {
              return { ...prev, passengers: { ...currentPassengers, [type]: current - 1 } };
            }
            return prev;
          });
        };

        return (
          <div className="flex flex-col h-full bg-zinc-950 overflow-hidden relative">
            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6 pb-32 custom-scrollbar">
              <h2 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                <Users className="w-6 h-6 text-emerald-400" />
                Composition du groupe
              </h2>

              <div className={`space-y-3 bg-zinc-800/60 p-3 rounded-xl border border-zinc-600/50`}>
                <div className="flex justify-between items-center mb-1">
                  <h3 className="text-base font-bold text-white flex items-center gap-2">👥 Passagers</h3>
                  <span className="text-xs font-bold text-emerald-400 bg-emerald-400/10 border border-emerald-400/20 px-2.5 py-1 rounded-full shadow-[0_0_8px_rgba(52,211,153,0.15)]">
                    {placesRestantes} place{placesRestantes > 1 ? 's' : ''} restante{placesRestantes > 1 ? 's' : ''}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  {[
                    { id: 'adults', label: 'Adultes' },
                    { id: 'children6_12', label: 'Enfants (6-12)' },
                    { id: 'children3_6', label: 'Enfants (3-6)' },
                    { id: 'babies', label: 'Bébés (-3)' },
                  ].map(cat => (
                    <div key={cat.id} className="flex items-center justify-between bg-zinc-900/80 p-2 rounded-lg border border-zinc-700">
                      <span className="text-xs text-zinc-100 font-semibold">{cat.label}</span>
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => updatePassenger(cat.id as any, false)}
                          disabled={(questionnaire.passengers || { seniors: 0, adults: 0, children6_12: 0, children3_6: 0, babies: 0 })[cat.id as keyof typeof questionnaire.passengers] <= 0}
                          className="w-6 h-6 rounded-full bg-zinc-700 text-white flex items-center justify-center disabled:opacity-50 transition-colors hover:bg-zinc-600 text-sm font-bold"
                        >-</button>
                        <span className="text-sm font-bold text-emerald-400 w-3 text-center">
                          {(questionnaire.passengers || { seniors: 0, adults: 0, children6_12: 0, children3_6: 0, babies: 0 })[cat.id as keyof typeof questionnaire.passengers]}
                        </span>
                        <button 
                          onClick={() => updatePassenger(cat.id as any, true)}
                          disabled={
                            placesRestantes <= 0 || 
                            (placesRestantes === 1 && 
                             questionnaire.passengers.seniors + questionnaire.passengers.adults === 0 && 
                             ['children6_12', 'children3_6', 'babies'].includes(cat.id))
                          }
                          className="w-6 h-6 rounded-full bg-zinc-700 text-white flex items-center justify-center disabled:opacity-50 transition-colors hover:bg-zinc-600 text-sm font-bold"
                        >+</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-1 space-y-3">
                <label className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${questionnaire.motionSickness ? 'bg-orange-500/20 border-orange-400 shadow-[0_0_12px_rgba(251,146,60,0.2)]' : 'bg-zinc-800/60 border-zinc-600/50 hover:bg-zinc-800 hover:border-zinc-500'}`}>
                  <div className={`w-5 h-5 rounded flex items-center justify-center border transition-colors ${questionnaire.motionSickness ? 'bg-orange-500 border-orange-400' : 'border-zinc-400 bg-zinc-900'}`}>
                    {questionnaire.motionSickness && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
                  </div>
                  <div className="flex-1">
                    <p className={`text-sm font-bold ${questionnaire.motionSickness ? 'text-orange-400' : 'text-white'}`}>Mal de la route</p>
                    <p className={`text-xs ${questionnaire.motionSickness ? 'text-orange-300/80' : 'text-zinc-400'}`}>Cochez si un passager est sensible</p>
                  </div>
                  <input 
                    type="checkbox" 
                    className="hidden"
                    checked={questionnaire.motionSickness || false}
                    onChange={(e) => setQuestionnaire(prev => ({ ...prev, motionSickness: e.target.checked }))}
                  />
                </label>

                <label className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${questionnaire.pmr ? 'bg-emerald-500/20 border-emerald-400 shadow-[0_0_12px_rgba(52,211,153,0.2)]' : 'bg-zinc-800/60 border-zinc-600/50 hover:bg-zinc-800 hover:border-zinc-500'}`}>
                  <div className={`w-5 h-5 rounded flex items-center justify-center border transition-colors ${questionnaire.pmr ? 'bg-emerald-500 border-emerald-400' : 'border-zinc-400 bg-zinc-900'}`}>
                    {questionnaire.pmr && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
                  </div>
                  <div className="flex-1">
                    <p className={`text-sm font-bold ${questionnaire.pmr ? 'text-emerald-400' : 'text-white'}`}>Personne à mobilité réduite (PMR)</p>
                    <p className={`text-xs ${questionnaire.pmr ? 'text-emerald-300/80' : 'text-zinc-400'}`}>Cochez si un passager est PMR</p>
                  </div>
                  <input 
                    type="checkbox" 
                    className="hidden"
                    checked={questionnaire.pmr || false}
                    onChange={(e) => setQuestionnaire(prev => ({ ...prev, pmr: e.target.checked }))}
                  />
                </label>
              </div>
            </div>

            <div className="absolute bottom-0 left-0 right-0 z-30 bg-zinc-950/90 backdrop-blur-xl border-t border-white/10">
              <div className="max-w-7xl mx-auto p-4 md:p-6 flex gap-3">
                <button onClick={handleBack} className="flex-1 py-3 bg-zinc-800 text-white rounded-xl font-semibold text-sm hover:bg-zinc-700 transition-colors border border-zinc-600">Retour</button>
                <button 
                  onClick={handleNext} 
                  disabled={!isStep4Valid}
                  className={`flex-1 py-3 rounded-xl font-semibold transition-all text-sm ${isStep4Valid ? 'bg-emerald-500 text-white hover:bg-emerald-400 shadow-[0_0_15px_rgba(52,211,153,0.4)]' : 'bg-zinc-800 text-zinc-500 border border-zinc-700 cursor-not-allowed'}`}
                >
                  Suivant
                </button>
              </div>
            </div>
          </div>
        );
      case 5:
        const categories = [
          { id: 'patrimoine', label: 'Patrimoine', icon: '🏛️' },
          { id: 'nature', label: 'Nature', icon: '🌳' },
          { id: 'activites', label: 'Activité / Sport', icon: '🚲' },
          { id: 'randonnee', label: 'Randonnée', icon: '🥾' },
          { id: 'points_de_vue', label: 'Points de vue', icon: '🔭' },
          { id: 'terroir', label: 'Terroir', icon: '🧀' },
          { id: 'grotte', label: 'Grotte', icon: '🪨' },
          { id: 'baignade', label: 'Baignade', icon: '🏊' },
          { id: 'detente', label: 'Détente', icon: '🧘' },
          { id: 'insolite', label: 'Insolite', icon: '❓' },
        ] as const;

        const togglePreference = (id: keyof typeof questionnaire.preferences) => {
          setQuestionnaire(prev => {
            const currentPrefs = { ...prev.preferences };
            const values = Object.values(currentPrefs).filter(v => v !== null) as number[];
            const nextRank = values.length > 0 ? Math.max(...values) + 1 : 1;
            
            if (currentPrefs[id] === null) {
              return { ...prev, preferences: { ...currentPrefs, [id]: nextRank } };
            } else {
              // Reset if clicked again
              const resetPrefs = { ...currentPrefs, [id]: null };
              // Re-rank remaining
              const remaining = Object.entries(resetPrefs).filter(([_, v]) => v !== null).sort((a, b) => (a[1] as number) - (b[1] as number));
              remaining.forEach(([key], index) => {
                resetPrefs[key as keyof typeof questionnaire.preferences] = index + 1;
              });
              return { ...prev, preferences: resetPrefs };
            }
          });
        };

        return (
          <div className="flex flex-col h-[100dvh] p-4 md:p-6 pb-24 overflow-hidden">
            <div className="shrink-0 space-y-1 mb-3">
              <h2 className="text-xl md:text-2xl font-bold text-white leading-tight">Préférences & Centres d'intérêt</h2>
              <p className="text-xs text-zinc-400">Cliquez sur les icônes pour classer par ordre de préférence</p>
            </div>
            
            <div className="flex-1 min-h-0 grid grid-cols-3 grid-rows-4 gap-2 md:gap-3">
          {categories.map((cat) => {
                const rank = questionnaire.preferences[cat.id];
                return (
                  <button
                    key={cat.id}
                    onClick={() => togglePreference(cat.id)}
                    className={`relative w-full h-full rounded-xl md:rounded-2xl flex flex-col items-center justify-center gap-2 transition-all p-2 ${
                      rank ? 'bg-emerald-500 text-white shadow-inner' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                    }`}
                  >
                    <span className="leading-none" style={{ fontSize: 'clamp(2rem, 6vh, 3.5rem)' }}>{cat.icon}</span>
                    <span className="text-[11px] md:text-sm font-bold text-center px-1 leading-tight">{cat.label}</span>
                    {rank && <span className="absolute -top-1 -right-1 md:-top-2 md:-right-2 bg-white text-emerald-500 w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center font-bold text-sm md:text-base shadow-lg">{rank}</span>}
                  </button>
                );
              })}
            </div>

            <div className="fixed bottom-0 left-0 right-0 z-30 bg-zinc-950/90 backdrop-blur-xl border-t border-white/10">
              <div className="max-w-7xl mx-auto p-4 md:p-6 flex gap-4">
                <button onClick={handleBack} className="flex-1 py-3 bg-zinc-800 text-white rounded-xl font-semibold text-sm hover:bg-zinc-700 transition-colors">Retour</button>
                <button 
                  onClick={handleNext} 
                  disabled={!Object.values(questionnaire.preferences).some(v => v !== null)}
                  className={`flex-[2] py-3 rounded-xl font-semibold text-sm transition-colors ${Object.values(questionnaire.preferences).some(v => v !== null) ? 'bg-emerald-500 text-white hover:bg-emerald-600' : 'bg-zinc-700 text-zinc-500 cursor-not-allowed'}`}
                >
                  Suivant
                </button>
              </div>
            </div>
          </div>
        );

        case 7:
        const sectorConfigs = settings.sectorConfigs || {};
        return (
          <div className="flex flex-col h-full p-0 overflow-hidden">
            <h2 className="text-xl md:text-2xl font-bold text-white mb-4 px-6 mt-2 shrink-0">Choisissez votre circuit</h2>
            <div className="flex-1 overflow-y-auto px-6 pb-24 grid grid-cols-1 md:grid-cols-2 gap-4 custom-scrollbar">
              {TOUR_SECTORS.map((key, index) => {
                const config = sectorConfigs[key] || { title: key, description: '' };
                const emblematicSite = sites.find(s => {
                  const siteSector = (s.sector || '').trim().toLowerCase().replace(/\s+/g, '');
                  const configKey = key.trim().toLowerCase().replace(/\s+/g, '');
                  return siteSector === configKey && s.isEmblematic;
                });
                const fallbackSite = sites.find(s => {
                  const siteSector = (s.sector || '').trim().toLowerCase().replace(/\s+/g, '');
                  const configKey = key.trim().toLowerCase().replace(/\s+/g, '');
                  return siteSector === configKey && s.image;
                });
                
                const displaySite = emblematicSite || fallbackSite;
                return (
                  <button
                    key={key}
                    onClick={() => {
                      setQuestionnaire(prev => ({ ...prev, sector: key }));
                      handleNext();
                    }}
                    className="w-full p-4 text-left transition-all flex flex-col gap-3 bg-zinc-900 hover:bg-zinc-800 border border-white/20 rounded-xl"
                  >
                    <div className="w-full h-32 md:h-40 rounded-lg bg-zinc-700 overflow-hidden shrink-0">
                      {(() => {
                        const isAiStudio = window.location.hostname.includes("run.app") || window.location.hostname.includes("localhost");
                        const sectorImg = (isAiStudio && config.imageLastUpdate) 
                          ? `/api/sector-image/${encodeURIComponent(key)}?t=${config.imageLastUpdate}`
                          : config.imageBase64;
                        
                        if (sectorImg) {
                          return (
                            <img 
                              src={sectorImg} 
                              alt={config.title} 
                              className="w-full h-full object-cover" 
                              referrerPolicy="no-referrer" 
                            />
                          );
                        } else if (displaySite?.images && displaySite.images.filter(img => !img.includes('maps.googleapis.com')).length > 0) {
                          return (
                            <div className="flex w-full h-full overflow-x-auto snap-x snap-mandatory scrollbar-hide">
                              {displaySite.images.filter(img => !img.includes('maps.googleapis.com')).map((img, idx) => (
                                <img key={idx} src={img} alt={`${displaySite.name} ${idx + 1}`} className="w-full h-full object-cover flex-shrink-0 snap-center touch-none" referrerPolicy="no-referrer" style={{ touchAction: 'none' }} />
                              ))}
                            </div>
                          );
                        } else if (displaySite?.image && !displaySite.image.includes('maps.googleapis.com')) {
                          return <img src={displaySite.image} alt={config.title} className="w-full h-full object-cover" referrerPolicy="no-referrer" />;
                        }
                        return <div className="w-full h-full flex items-center justify-center text-xl">❓</div>;
                      })()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-white text-base md:text-lg leading-tight">{config.title}</h3>
                      <p className="text-xs text-zinc-400 line-clamp-4 leading-tight">{config.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="absolute bottom-0 left-0 right-0 z-30 bg-zinc-950/90 backdrop-blur-xl border-t border-white/10">
              <div className="max-w-7xl mx-auto p-4 flex justify-center">
                <button onClick={handleBack} className="px-8 py-2 bg-zinc-800 text-zinc-300 text-sm rounded-full hover:bg-zinc-700 transition-colors border border-zinc-700">Retour</button>
              </div>
            </div>
          </div>
        );
      case 8:
        const sectorKey = questionnaire.sector || "";
        const config = settings.sectorConfigs?.[sectorKey] || { title: sectorKey, description: '' };
        
        // Find all emblematic sites for this sector
        const emblematicSites = sites.filter(s => {
          const siteSector = (s.sector || '').trim().toLowerCase().replace(/\s+/g, '');
          const configKey = sectorKey.trim().toLowerCase().replace(/\s+/g, '');
          return siteSector === configKey && s.isEmblematic;
        }).slice(0, 8); // Limit to 8 as requested
        
        return (
          <div className="flex flex-col h-full p-0 bg-zinc-950 overflow-hidden relative">
            {/* Fixed Carousel Header */}
            <div className="relative h-56 md:h-72 w-full shrink-0 overflow-hidden" ref={sectorEmblaRef}>
              <div className="flex h-full">
                {emblematicSites.length > 0 ? (
                  emblematicSites.map((site, idx) => (
                    <div key={idx} className="flex-[0_0_100%] h-full relative">
                      <img 
                        src={site.images?.[0] || site.image} 
                        alt={site.name} 
                        className="w-full h-full object-cover" 
                        referrerPolicy="no-referrer" 
                      />
                      <div className="absolute bottom-4 left-6 bg-black/40 backdrop-blur-sm px-3 py-1 rounded-lg">
                        <p className="text-xs text-white/90 font-medium">{site.name}</p>
                      </div>
                    </div>
                  ))
                ) : config.imageBase64 || config.imageLastUpdate ? (
                  <div className="flex-[0_0_100%] h-full">
                    <img 
                      src={config.imageBase64 || `/api/sector-image/${encodeURIComponent(sectorKey)}?t=${config.imageLastUpdate || Date.now()}`} 
                      alt={config.title} 
                      className="w-full h-full object-cover" 
                      referrerPolicy="no-referrer" 
                    />
                  </div>
                ) : (
                  <div className="flex-[0_0_100%] h-full bg-zinc-800 flex items-center justify-center text-4xl">📸</div>
                )}
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-transparent to-transparent pointer-events-none" />
            </div>
            
            {/* Scrollable Content Area */}
            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6 pb-32 custom-scrollbar">
              <div className="space-y-2">
                <h2 className="text-xl md:text-2xl font-vtc font-semibold text-white">{config.title}</h2>
                <div className="h-px w-12 bg-emerald-500/50" />
              </div>
              
              <p className="text-sm md:text-base text-zinc-300 leading-relaxed whitespace-pre-wrap">
                {config.description}
              </p>
            </div>

            <div className="absolute bottom-0 left-0 right-0 z-30 bg-zinc-950/90 backdrop-blur-xl border-t border-white/10">
              <div className="max-w-7xl mx-auto p-6 flex gap-4">
                <button onClick={handleBack} className="flex-1 py-3 bg-zinc-800 text-white rounded-xl font-semibold text-sm hover:bg-zinc-700 transition-colors">Retour</button>
                <button onClick={handleNext} className="flex-[2] py-3 bg-emerald-500 text-white rounded-xl font-semibold text-sm hover:bg-emerald-600 transition-colors">Valider ce secteur</button>
              </div>
            </div>
          </div>
        );
      case 9:
        return (
          <Step9
            questionnaire={questionnaire}
            setQuestionnaire={setQuestionnaire}
            sites={sites}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            hideUnsuitable={hideUnsuitable}
            setHideUnsuitable={setHideUnsuitable}
            setSelectedActivity={setSelectedActivity}
            setPreviousStep={setPreviousStep}
            setStep={setStep}
            handleBack={handleBack}
            handleNext={handleNext}
            CATEGORIES={CATEGORIES}
            getSiteScore={getSiteScore}
            getScoreColor={getScoreColor}
            getScoreBadge={getScoreBadge}
            calculateLocalCost={calculateLocalCost}
          />
        );
      case 10.1:
        return (
          <Step10_1
            questionnaire={questionnaire}
            setQuestionnaire={setQuestionnaire}
            sites={sites}
            settings={settings}
            idealDays={idealDays}
            handleBack={handleBack}
            onComplete={(circuit) => {
              // onComplete from Step10_1 triggers circuit generation
              handleNext(true);
            }}
            getSiteScore={getSiteScore}
            getScoreColor={getScoreColor}
            getScoreBadge={getScoreBadge}
            setSelectedActivity={setSelectedActivity}
            setPreviousStep={setPreviousStep}
            setStep={setStep}
          />
        );
      case 9.1:
        return (
          <Step9_1
            questionnaire={questionnaire}
            setQuestionnaire={setQuestionnaire}
            sites={sites}
            settings={settings}
            driverHomeLocation={driverHomeLocation}
            idealDays={idealDays}
            setStep={setStep}
            handleNext={handleNext}
            handleBack={handleBack}
          />
        );
      case 9.2:
        return (
          <Step9_2
            questionnaire={questionnaire}
            setQuestionnaire={(updater) => {
              setQuestionnaire(prev => {
                const next = typeof updater === 'function' ? updater(prev) : updater;
                return sanitizeQuestionnaire(next, prev);
              });
            }}
            setStep={setStep}
            handleBack={handleBack}
          />
        );
      case 9.3:
        return (
          <Step9_3
            questionnaire={questionnaire}
            setQuestionnaire={setQuestionnaire}
            currentDay={currentPlanningDay}
            currentMoment={currentMoment}
            handleNext={handleNext}
            handleBack={handleBack}
          />
        );
      case 9.5:
        const sitesToRank = questionnaire.selectedActivities.map(id => sites.find(s => s.id === id)).filter(Boolean) as TouristSite[];
        return (
          <Step9_5
            sitesToRank={sitesToRank}
            orderedIds={orderedIds}
            setOrderedIds={setOrderedIds}
            isHeavy={isHeavy}
            questionnaire={questionnaire}
            settings={settings}
            driverHomeLocation={driverHomeLocation}
            idealDays={idealDays}
            handleNext={handleNext}
            handleBack={handleBack}
            setSelectedActivity={setSelectedActivity}
            setPreviousStep={setPreviousStep}
            setStep={setStep}
          />
        );
      case 9.6:
        return (
          <Step9_6
            questionnaire={questionnaire}
            setQuestionnaire={setQuestionnaire}
            sites={sites}
            currentDay={currentPlanningDay}
            moment={currentMoment}
            setSelectedActivity={setSelectedActivity}
            setPreviousStep={setPreviousStep}
            setStep={setStep}
            handleNext={handleNext}
            handleBack={handleBack}
            getRestaurantScore={getRestaurantScore}
            proposedCircuit={proposedCircuit}
          />
        );
      case 9.7:
        return (
          <Step9_7
            questionnaire={questionnaire}
            setQuestionnaire={setQuestionnaire}
            sites={sites}
            currentDay={currentPlanningDay}
            setSelectedActivity={setSelectedActivity}
            setPreviousStep={setPreviousStep}
            setStep={setStep}
            handleNext={handleNext}
            handleBack={handleBack}
            proposedCircuit={proposedCircuit}
          />
        );
      case 9.8:
        return (
          <Step9_8
            currentPlanningDay={currentPlanningDay}
            proposedCircuit={proposedCircuit}
            handleNext={handleNext}
            handleBack={handleBack}
            totalDays={questionnaire.logistics?.days || 1}
          />
        );
      case 10:
        if (!selectedActivity) return null;
        return (
          <div className="flex flex-col h-full p-0 bg-zinc-950 overflow-hidden relative">
            {/* Header with large photo carousel */}
            <div className="relative h-64 md:h-80 w-full shrink-0 overflow-hidden" ref={emblaRef}>
              <div className="flex h-full">
                {selectedActivity.images && selectedActivity.images.filter(img => !img.includes('maps.googleapis.com')).length > 0 ? (
                  selectedActivity.images.filter(img => !img.includes('maps.googleapis.com')).map((img, idx) => (
                    <div key={idx} className="flex-[0_0_100%] h-full">
                      <img src={img} alt={`${selectedActivity.name} ${idx + 1}`} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                    </div>
                  ))
                ) : selectedActivity.image && !selectedActivity.image.includes('maps.googleapis.com') ? (
                  <div className="flex-[0_0_100%] h-full">
                    <img src={selectedActivity.image} alt={selectedActivity.name} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                  </div>
                ) : (
                  <div className="flex-[0_0_100%] h-full bg-zinc-800 flex items-center justify-center text-4xl">📸</div>
                )}
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-transparent to-transparent" />
              <button 
                onClick={handleBack}
                className="absolute top-4 left-4 p-2 bg-black/50 backdrop-blur-md rounded-full text-white hover:bg-black/70 transition-colors z-10"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-8 pb-32 custom-scrollbar">
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-emerald-400 text-[10px] font-bold uppercase tracking-widest">
                  <span>{selectedActivity.sector}</span>
                  <span>•</span>
                  <span>{(selectedActivity.categories || []).join(' • ')}</span>
                </div>
                <h2 className="text-2xl md:text-3xl font-vtc font-semibold text-white leading-tight">{selectedActivity.name}</h2>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-1 text-amber-400">
                    <Star className="w-4 h-4 fill-current" />
                    <span className="text-sm font-bold">{selectedActivity.rating || selectedActivity.googleRating || "N/A"}</span>
                  </div>
                  <span className="text-sm text-zinc-500">({selectedActivity.userRatingsTotal || selectedActivity.googleReviewsCount || 0} avis Google)</span>
                  
                  <div className="flex items-center gap-1.5 ml-2">
                    {selectedActivity.isHandicapAccessible && (
                      <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-500/20 border border-blue-500/30" title="Accessible PMR">
                        <Accessibility className="w-3.5 h-3.5 text-blue-400" />
                      </div>
                    )}
                    {selectedActivity.dogsNotAllowed && (
                      <div className="relative flex items-center justify-center w-6 h-6 rounded-full bg-red-500/20 border border-red-500/30" title="Chiens interdits">
                        <PawPrint className="w-3.5 h-3.5 text-red-400" />
                        <Ban className="w-5 h-5 text-red-500 absolute opacity-50" strokeWidth={1} />
                      </div>
                    )}
                    {selectedActivity.notSuitableForChildren && (
                      <div className="relative flex items-center justify-center w-6 h-6 rounded-full bg-red-500/20 border border-red-500/30" title="Inadapté aux enfants">
                        <Baby className="w-3.5 h-3.5 text-red-400" />
                        <Ban className="w-5 h-5 text-red-500 absolute opacity-50" strokeWidth={1} />
                      </div>
                    )}
                    {selectedActivity.isWindingRoad && (
                      <div className="flex items-center justify-center w-6 h-6 rounded-full bg-amber-500/20 border border-amber-500/30" title="Route sinueuse">
                        <Route className="w-3.5 h-3.5 text-amber-400" />
                      </div>
                    )}
                  </div>
                  
                  {(selectedActivity.categories || []).includes('restauration') && (
                    <div className="bg-amber-500/20 border border-amber-500/30 text-amber-400 px-2 py-0.5 rounded-lg font-bold text-xs ml-2">
                      {selectedActivity.forks === 0 ? 'Snack' : 'Restaurant'}
                    </div>
                  )}
                </div>
              </div>

              {/* Detailed Description */}
              <div className="space-y-4">
                {/* Avis de l'algorithme (Scoring) */}
                {(() => {
                  const isRestaurant = (selectedActivity.categories || []).includes('restauration') || selectedActivity.cateringType === 'restaurant' || selectedActivity.cateringType === 'snack';
                  const scoreInfo = isRestaurant && previousStep === 9.6 
                    ? getRestaurantScore(selectedActivity) 
                    : (() => {
                        const siteScore = getSiteScore(selectedActivity);
                        const color = siteScore.forcedColor || (siteScore.score >= 1000 ? 'green' : siteScore.score >= 500 ? 'yellow' : siteScore.score >= 100 ? 'orange' : 'red');
                        return {
                          color,
                          reason: siteScore.reasons.join(' • ')
                        };
                      })();

                  if (!scoreInfo.reason) return null;

                  return (
                    <div className={`rounded-2xl p-4 mb-2 border ${
                      scoreInfo.color === 'green' ? 'bg-emerald-500/10 border-emerald-500/20' :
                      scoreInfo.color === 'yellow' ? 'bg-yellow-400/10 border-yellow-400/20' :
                      scoreInfo.color === 'orange' ? 'bg-orange-500/10 border-orange-500/20' :
                      'bg-red-500/10 border-red-500/20'
                    }`}>
                      <div className="flex items-center gap-3">
                        {getScoreBadge(scoreInfo.color as string)}
                        <p className="text-zinc-300 text-sm leading-relaxed">
                          {scoreInfo.reason}
                        </p>
                      </div>
                    </div>
                  );
                })()}

                <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap">
                  {selectedActivity.fullDescription || selectedActivity.description}
                </p>
              </div>

              {/* Practical Info & Prices */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <Clock className="w-5 h-5 text-emerald-500" />
                    Infos pratiques
                  </h3>
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-4 space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-zinc-500">Durée de visite</span>
                      <span className="text-white font-medium">{selectedActivity.durationMinutes || 60} min</span>
                    </div>
                    {selectedActivity.phone && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-zinc-500">Téléphone</span>
                        <a href={`tel:${selectedActivity.phone}`} className="text-emerald-400 font-medium hover:underline">{selectedActivity.phone}</a>
                      </div>
                    )}
                    {selectedActivity.website && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-zinc-500">Site web</span>
                        <a href={selectedActivity.website} target="_blank" rel="noopener noreferrer" className="text-emerald-400 font-medium hover:underline flex items-center gap-1">
                          Visiter <Globe className="w-3 h-3" />
                        </a>
                      </div>
                    )}
                    {(selectedActivity.address || selectedActivity.openingHours || selectedActivity.conditions || selectedActivity.specialConditions) && (
                      <div className="pt-2 border-t border-white/5 space-y-3">
                        {selectedActivity.address && (
                          <div className="flex items-start gap-2 text-sm">
                            <MapPin className="w-4 h-4 text-zinc-500 shrink-0 mt-0.5" />
                            <span className="text-zinc-300">{selectedActivity.address}</span>
                          </div>
                        )}
                        {selectedActivity.openingHours && (
                          <div className="flex items-start gap-2 text-sm">
                            <Clock className="w-4 h-4 text-zinc-500 shrink-0 mt-0.5" />
                            <span className="text-zinc-300">{selectedActivity.openingHours}</span>
                          </div>
                        )}
                        {selectedActivity.conditions && (
                          <div className="flex items-start gap-2 text-sm">
                            <FileText className="w-4 h-4 text-zinc-500 shrink-0 mt-0.5" />
                            <span className="text-zinc-300"><strong>Conditions :</strong> {selectedActivity.conditions}</span>
                          </div>
                        )}
                        {selectedActivity.specialConditions && selectedActivity.specialConditions !== selectedActivity.conditions && (
                          <div className="flex items-start gap-2 text-sm">
                            <AlertTriangle className="w-4 h-4 text-zinc-500 shrink-0 mt-0.5" />
                            <span className="text-zinc-300"><strong>Spécifique :</strong> {selectedActivity.specialConditions}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {!selectedActivity.isFree && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                      <Coins className="w-5 h-5 text-emerald-500" />
                      Tarifs indicatifs
                    </h3>
                    <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-4 space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-zinc-500">Adulte</span>
                        <span className="text-white font-bold">{selectedActivity.adultPrice || 0} €</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-zinc-500">Enfant (6-12 ans)</span>
                        <span className="text-white font-bold">{selectedActivity.child6_12Price || 0} €</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-zinc-500">Enfant (3-6 ans)</span>
                        <span className="text-white font-bold">{selectedActivity.child3_6Price || 0} €</span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-zinc-500">Bébé (-3 ans)</span>
                        <span className="text-white font-bold">{selectedActivity.babyPrice || 0} €</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {/* Bottom Action Bar */}
            <div className="absolute bottom-0 left-0 right-0 z-30 bg-zinc-950/90 backdrop-blur-xl border-t border-white/10">
              <div className="max-w-7xl mx-auto p-6 flex gap-4">
              <button 
                onClick={handleBack}
                className="flex-1 py-3 bg-zinc-800 text-white rounded-xl font-semibold text-sm hover:bg-zinc-700 transition-colors"
              >
                Retour
              </button>
              <button 
                disabled={!detailReady}
                onClick={() => {
                  if (previousStep === 9.6) {
                    const currentDay = questionnaire.logistics?.days > 1 ? (questionnaire.currentRestaurantDay || 1) : 1;
                    const selectedRestaurants = { ...(questionnaire.selectedRestaurants || {}) };
                    if (!selectedRestaurants[currentDay]) selectedRestaurants[currentDay] = {};
                    selectedRestaurants[currentDay][currentMoment] = selectedActivity.id;
                    setQuestionnaire(prev => ({ ...prev, selectedRestaurants }));
                    handleBack();
                  } else {
                    setQuestionnaire(prev => {
                      const isSelected = prev.selectedActivities.includes(selectedActivity.id);
                      return {
                        ...prev,
                        selectedActivities: isSelected 
                          ? prev.selectedActivities.filter(id => id !== selectedActivity.id)
                          : [...prev.selectedActivities, selectedActivity.id]
                      };
                    });
                    handleBack();
                  }
                }}
                className={`flex-[2] py-3 text-white rounded-xl font-semibold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  previousStep === 9.6 
                    ? (questionnaire.selectedRestaurants?.[questionnaire.logistics?.days > 1 ? (questionnaire.currentRestaurantDay || 1) : 1]?.[currentMoment] === selectedActivity.id
                        ? 'bg-emerald-600 shadow-[0_0_20px_rgba(16,185,129,0.3)]'
                        : 'bg-emerald-500 hover:bg-emerald-600 shadow-[0_0_20px_rgba(16,185,129,0.3)]')
                    : (questionnaire.selectedActivities.includes(selectedActivity.id)
                      ? 'bg-red-500 hover:bg-red-600 shadow-[0_0_20px_rgba(239,68,68,0.3)]'
                      : 'bg-emerald-500 hover:bg-emerald-600 shadow-[0_0_20px_rgba(16,185,129,0.3)]')
                }`}
              >
                {previousStep === 9.6 
                  ? (questionnaire.selectedRestaurants?.[questionnaire.logistics?.days > 1 ? (questionnaire.currentRestaurantDay || 1) : 1]?.[currentMoment] === selectedActivity.id ? 'Restaurant sélectionné' : 'Sélectionner ce restaurant')
                  : (questionnaire.selectedActivities.includes(selectedActivity.id) ? 'Retirer de ma sélection' : 'Sélectionner cette activité')}
              </button>
            </div>
          </div>
        </div>
      );
      case 11:
        if (!proposedCircuit) return null;
        if (proposedCircuit.error) {
          return (
            <div className="flex flex-col items-center justify-center p-10 space-y-4 text-center">
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center border border-red-500/30">
                <Ban className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="text-xl font-bold text-white">Oups !</h2>
              <p className="text-zinc-400 max-w-md">{proposedCircuit.error}</p>
              <button 
                onClick={() => setStep(9)}
                className="px-5 py-2.5 bg-zinc-800 text-white rounded-xl font-semibold text-sm hover:bg-zinc-700 transition-colors"
              >
                Modifier ma sélection
              </button>
            </div>
          );
        }

        const totalDays = questionnaire.logistics?.days || 1;
        
        // Group steps by day
        const dayStepsMap: Record<number, any[]> = {};
        let currentDay = 0;
        proposedCircuit.steps.forEach((step: any) => {
          if (step.type === 'pickup') currentDay++;
          if (!dayStepsMap[currentDay]) dayStepsMap[currentDay] = [];
          dayStepsMap[currentDay].push(step);
        });

        const currentSteps = dayStepsMap[currentPlanningDay] || [];

        return (
          <div className="flex flex-col h-full overflow-hidden">
            <div className="flex-1 overflow-y-auto px-1 custom-scrollbar space-y-8 pb-32">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h2 className="text-3xl font-bold text-white tracking-tight">Votre Itinéraire</h2>
                  <div className="bg-emerald-500 text-zinc-950 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest shadow-lg shadow-emerald-500/20">
                    Jour {currentPlanningDay} / {totalDays}
                  </div>
                </div>
                <p className="text-zinc-400">Optimisé pour vos préférences et contraintes de temps.</p>
              </div>

              {/* Timeline */}
              <div className="relative space-y-0 pl-6 border-l-2 border-emerald-500/30 ml-4">
                {currentSteps.map((step: any, idx: number) => {
                  return (
                    <div key={idx} className="relative pb-8 last:pb-0">
                      {/* Dot */}
                      <div className={cn(
                        "absolute -left-[33px] top-0 w-4 h-4 rounded-full border-2 border-zinc-950",
                        step.type === 'pickup' || step.type === 'dropoff' ? "bg-emerald-500" :
                        step.type === 'meal' ? "bg-amber-500" :
                        step.type === 'activity' ? "bg-blue-500" : "bg-zinc-600"
                      )} />
                      
                      <div className="flex flex-col gap-1">
                        {step.label && (
                          <div className="flex items-center gap-3">
                            <span className="text-xs font-mono text-emerald-400 font-bold bg-emerald-400/10 px-2 py-0.5 rounded">
                              {step.startTime}
                            </span>
                            <h3 className={cn(
                              "font-bold",
                              step.type === 'activity' ? "text-white text-lg" : "text-zinc-300"
                            )}>
                              {step.label}
                            </h3>
                          </div>
                        )}

                      {step.type === 'travel' && (
                        <div className="flex items-center gap-2 text-[10px] text-zinc-500 ml-14 -mt-4 mb-4">
                          <Route className="w-3 h-3" />
                          <span>{Math.round(step.distanceKm || 0)} km • {step.durationMinutes} min de trajet</span>
                        </div>
                      )}

                      {(step.type === 'activity' || step.type === 'meal' || (step.site && (step.type === 'pickup' || step.type === 'dropoff'))) && (
                        <div className={cn(
                          "mt-2 p-4 rounded-2xl border flex flex-col gap-4 group relative",
                          step.type === 'meal' ? "bg-amber-500/5 border-amber-500/20" : 
                          (step.type === 'pickup' || step.type === 'dropoff') ? "bg-blue-500/5 border-blue-500/20" :
                          "bg-zinc-900/50 border-white/5"
                        )}>
                          {step.site ? (
                            <div className="flex gap-4 items-start">
                              <div className="w-20 h-20 rounded-xl bg-zinc-800 overflow-hidden shrink-0 border border-white/10 relative">
                                {step.site.image && !step.site.image.includes('maps.googleapis.com') ? (
                                  <img 
                                    src={step.site.image} 
                                    alt={step.site.name} 
                                    className="w-full h-full object-cover"
                                    referrerPolicy="no-referrer"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-3xl">
                                    {step.type === 'meal' ? '🍽️' : (step.type === 'pickup' || step.type === 'dropoff') ? '🏨' : '📍'}
                                  </div>
                                )}
                                {step.type === 'meal' && (
                                  <div className="absolute top-0 left-0 bg-amber-500 px-1.5 py-0.5 rounded-br-lg text-[8px] font-bold text-zinc-950 uppercase tracking-tighter">
                                    {step.site.forks === 0 ? 'Snack' : 'Resto'}
                                  </div>
                                )}
                                {(step.type === 'pickup' || step.type === 'dropoff') && (
                                  <div className="absolute top-0 left-0 bg-blue-500 px-1.5 py-0.5 rounded-br-lg text-[8px] font-bold text-white uppercase tracking-tighter">
                                    Logement
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex justify-between items-start">
                                  <div className="flex flex-col">
                                    <h4 className="text-white font-bold text-sm leading-tight pr-8">{step.site.name}</h4>
                                    {step.type === 'meal' && step.detourKm > 0 && (
                                      <div className={cn(
                                        "mt-1 flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-bold w-fit",
                                        step.detourKm > 15 ? "bg-red-500/20 text-red-400 border border-red-500/30" : "bg-amber-500/20 text-amber-400 border border-amber-500/30"
                                      )}>
                                        <AlertTriangle className="w-3 h-3" />
                                        <span>Détour de {Math.round(step.detourKm)} km</span>
                                      </div>
                                    )}
                                    <div className="flex items-center gap-2 mt-1">
                                      <div className="flex items-center gap-1 text-amber-400">
                                        <Star className="w-3 h-3 fill-current" />
                                        <span className="text-[10px] font-bold">{step.site.rating || step.site.googleRating || "N/A"}</span>
                                      </div>
                                      <span className="text-[10px] text-zinc-500">({step.site.userRatingsTotal || step.site.googleReviewsCount || 0} avis)</span>
                                    </div>
                                  </div>
                                  {(step.type === 'activity' || step.type === 'meal') && (
                                    <button 
                                      onClick={() => {
                                        if (step.type === 'activity') {
                                          const newSelection = questionnaire.selectedActivities.filter(id => id !== step.site!.id);
                                          setQuestionnaire(prev => ({ ...prev, selectedActivities: newSelection }));
                                          const selectedSites = newSelection.map(id => sites.find(s => s.id === id)).filter(Boolean) as TouristSite[];
                                          const result = generateCircuit(questionnaire, selectedSites, settings, driverHomeLocation, sites);
                                          setProposedCircuit(result);
                                        } else {
                                          const selectedRestaurants = { ...(questionnaire.selectedRestaurants || {}) };
                                          const day = proposedCircuit.steps.slice(0, idx + 1).filter((s: any) => s.type === 'pickup').length;
                                          const moment = step.label?.includes('Dîner') ? 'dinner' : 'lunch';
                                          if (selectedRestaurants[day]) {
                                            delete selectedRestaurants[day][moment];
                                            setQuestionnaire(prev => ({ ...prev, selectedRestaurants }));
                                            const selectedSites = questionnaire.selectedActivities.map(id => sites.find(s => s.id === id)).filter(Boolean) as TouristSite[];
                                            const result = generateCircuit({ ...questionnaire, selectedRestaurants }, selectedSites, settings, driverHomeLocation, sites);
                                            setProposedCircuit(result);
                                          }
                                        }
                                      }}
                                      className="absolute top-4 right-4 p-2 bg-red-500/20 text-red-500 rounded-xl hover:bg-red-500 hover:text-white transition-all active:scale-95 z-10"
                                      title="Retirer de l'itinéraire"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                                
                                <div className="flex flex-wrap items-center gap-3 mt-3">
                                  {step.durationMinutes > 0 && (
                                    <div className="flex items-center gap-1.5 px-2 py-1 bg-white/5 rounded-lg border border-white/5">
                                      <Clock className="w-3 h-3 text-zinc-400" />
                                      <span className="text-[10px] font-bold text-zinc-300">{step.durationMinutes} min</span>
                                    </div>
                                  )}
                                  
                                  {step.site.isHandicapAccessible && (
                                    <Accessibility className="w-3.5 h-3.5 text-blue-400" />
                                  )}
                                  {step.site.dogsNotAllowed ? (
                                    <div className="relative">
                                      <PawPrint className="w-3.5 h-3.5 text-red-400 opacity-50" />
                                      <Ban className="w-4 h-4 text-red-500 absolute -top-0.5 -left-0.5" strokeWidth={1} />
                                    </div>
                                  ) : (
                                    <PawPrint className="w-3.5 h-3.5 text-emerald-400" />
                                  )}
                                  {step.site.isWindingRoad && (
                                    <Route className="w-3.5 h-3.5 text-orange-400" />
                                  )}
                                  {step.site.notSuitableForChildren && (
                                    <div className="flex items-center gap-1 px-1.5 py-0.5 bg-red-500/10 border border-red-500/20 rounded text-[8px] font-bold text-red-400 uppercase">
                                      Inadapté enfants
                                    </div>
                                  )}
                                </div>

                                <div className="mt-3 flex items-center justify-between pt-3 border-t border-white/5">
                                  <div className="flex gap-1">
                                    {step.type === 'meal' && step.site.forks > 0 && (
                                      <div className="flex gap-0.5">
                                        {[...Array(step.site.forks)].map((_, i) => (
                                          <UtensilsCrossed key={i} className="w-3 h-3 text-blue-400" />
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                  <div className="text-right">
                                    <span className="text-[10px] text-zinc-500 block">Prix estimé</span>
                                    <span className="text-xs font-bold text-emerald-400">{calculateSitePrice(step.site)} €</span>
                                  </div>
                                </div>

                                {step.type === 'meal' && step.distanceKm > 0 && (
                                  <div className="mt-2 p-2 bg-orange-500/10 border border-orange-500/20 rounded-lg flex items-center gap-2">
                                    <AlertTriangle className="w-3.5 h-3.5 text-orange-400" />
                                    <p className="text-[10px] font-bold text-orange-200">
                                      Attention : ce restaurant nécessite un détour de {Math.round(step.distanceKm)} km.
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center gap-4">
                              <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
                                <Utensils className="w-6 h-6 text-amber-500" />
                              </div>
                              <div>
                                <p className="text-sm font-bold text-amber-200">{step.label}</p>
                                <p className="text-xs text-amber-500/70">{step.durationMinutes} minutes prévues</p>
                              </div>
                            </div>
                          )}
                          
                          {step.site && (step.site.conditions || step.site.specialConditions || (step.site.categories && step.site.categories.some(c => settings.categoryConditions?.[c]))) && (
                            <div className="p-3 bg-amber-500/5 border border-amber-500/10 rounded-xl space-y-2">
                              <div className="flex items-center gap-2 text-amber-400 text-[10px] font-bold uppercase tracking-widest">
                                <AlertTriangle className="w-3 h-3" />
                                {step.type === 'meal' ? 'Infos Pratiques' : 'Conseils & Équipements'}
                              </div>
                              <div className="text-[11px] text-amber-200/80 space-y-1">
                                {step.site.conditions && <p>• {step.site.conditions}</p>}
                                {step.site.specialConditions && step.site.specialConditions !== step.site.conditions && <p>• {step.site.specialConditions}</p>}
                                {step.site.categories?.map(cat => {
                                  const cond = settings.categoryConditions?.[cat];
                                  if (cond) return <p key={cat}>• {cond}</p>;
                                  return null;
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Summary Card */}
              <div className="p-6 bg-zinc-900 rounded-3xl border border-white/10 space-y-6 shadow-2xl relative">
                <button 
                  onClick={() => {
                    const selectedSites = questionnaire.selectedActivities.map(id => sites.find(s => s.id === id)).filter(Boolean) as TouristSite[];
                    const result = generateCircuit(questionnaire, selectedSites, settings, driverHomeLocation);
                    setProposedCircuit(result);
                  }}
                  className="absolute top-4 right-4 p-2 bg-emerald-500/10 text-emerald-500 rounded-xl hover:bg-emerald-500 hover:text-white transition-all flex items-center gap-2 text-[10px] font-semibold"
                  title="Recalculer l'itinéraire"
                >
                  <RefreshCw className="w-3 h-3" />
                  RECALCULER
                </button>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-zinc-800/50 rounded-2xl border border-white/5 space-y-1">
                    <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Distance totale</p>
                    <p className="text-xl font-bold text-white">{Math.round(proposedCircuit.totalKm)} km</p>
                  </div>
                  <div className="p-4 bg-zinc-800/50 rounded-2xl border border-white/5 space-y-1">
                    <p className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest">Durée totale</p>
                    <p className="text-xl font-bold text-white">
                      {Math.floor(proposedCircuit.totalDuration / 60)}h{proposedCircuit.totalDuration % 60}
                    </p>
                  </div>
                </div>

                <div className="pt-6 border-t border-white/5 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-1">Tarif estimé (Tout inclus)</p>
                    <p className="text-3xl font-bold text-emerald-400">{proposedCircuit.price} €</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] text-zinc-500 italic">Comprend le transport (cumulé),</p>
                    <p className="text-[10px] text-zinc-500 italic">le chauffeur, et les activités.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Bar */}
            <div className="absolute bottom-0 left-0 right-0 z-30 bg-zinc-950/90 backdrop-blur-xl border-t border-white/10">
              <div className="max-w-7xl mx-auto p-6 flex gap-4">
                <button 
                  onClick={() => {
                    if (currentPlanningDay > 1) {
                      setCurrentPlanningDay(prev => prev - 1);
                    } else {
                      // Reset planning state, keep all user choices for editing
                      setProposedCircuit(null);
                      setIsHeavy(false);
                      setCurrentPlanningDay(1);
                      setCurrentMoment('lunch');
                      setStep(9);
                    }
                  }}
                  className="flex-1 py-3 bg-zinc-800 text-white rounded-xl font-semibold text-sm hover:bg-zinc-700 transition-colors"
                >
                  {currentPlanningDay > 1 ? 'Jour précédent' : 'Modifier'}
                </button>
                <button 
                  onClick={() => {
                    const totalDays = questionnaire.logistics?.days || 1;
                    if (currentPlanningDay < totalDays) {
                      setCurrentPlanningDay(prev => prev + 1);
                      // Scroll to top
                      const scrollContainer = document.querySelector('.custom-scrollbar');
                      if (scrollContainer) scrollContainer.scrollTop = 0;
                    } else {
                      handleNext(true);
                    }
                  }}
                  className="flex-[2] py-3 bg-emerald-500 text-white rounded-xl font-semibold text-sm hover:bg-emerald-600 shadow-[0_0_20px_rgba(16,185,129,0.3)] transition-all"
                >
                  {currentPlanningDay < (questionnaire.logistics?.days || 1) ? `Valider le Jour ${currentPlanningDay}` : 'Valider et réserver'}
                </button>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="w-full max-w-7xl mx-auto h-full flex flex-col overflow-hidden relative">
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
          className="flex-1 relative flex flex-col h-full w-full overflow-hidden"
        >
          {renderStep()}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};
